<script setup lang="ts">
import { Link, router } from "@inertiajs/vue3"
import AppShell from "../Layout/AppShell.vue"
import { UiButton } from "@/components"
import { useConfirmation } from "@/composables/useConfirmation"
import { useNotifications } from "@/composables/useNotifications"

const { confirm } = useConfirmation()
const notify = useNotifications()

type TaxProfileRow = {
  id: number
  company_id: number
  company: string
  name: string
  active: boolean
  default: boolean
  credit_icms: boolean
  credit_pis_cofins: boolean
  ipi_included_in_cost: boolean
  estimated_sales_tax_percent: string
  can_edit: boolean
  can_destroy: boolean
}

defineProps<{
  tax_profiles?: TaxProfileRow[]
  companies: Array<{ id: number; name: string }>
  can_create: boolean
}>()

async function destroyProfile(id: number) {
  const ok = await confirm({
    title: "Excluir perfil fiscal",
    message: "Deseja excluir este perfil fiscal? Esta acao nao pode ser desfeita.",
    type: "danger",
    confirmText: "Excluir",
  })
  if (!ok) return

  router.delete(`/tax_profiles/${id}`, {
    preserveScroll: true,
    onSuccess: () => notify.success("Perfil fiscal excluído com sucesso"),
  })
}
</script>

<template>
  <AppShell>
    <div class="mx-auto max-w-7xl space-y-6">
      <section class="rounded-lg border border-slate-200 bg-white p-6 shadow-sm">
        <div class="flex items-center justify-between">
          <div>
            <h1 class="text-2xl font-semibold text-slate-900">Perfis Fiscais</h1>
            <p class="mt-2 text-sm text-slate-600">Configure parametros fiscais por empresa para compras e estimativas de venda.</p>
          </div>
          <Link
            v-if="can_create"
            href="/tax_profiles/new"
            class="rounded-md bg-slate-900 px-4 py-2 text-sm font-medium text-white transition hover:bg-slate-800"
          >
            Novo perfil
          </Link>
        </div>
      </section>

      <section class="rounded-lg border border-slate-200 bg-white shadow-sm">
        <div class="overflow-x-auto">
          <table class="min-w-full divide-y divide-slate-200 text-sm">
            <thead class="bg-slate-50 text-slate-700">
              <tr>
                <th class="px-4 py-3 text-left font-medium">Empresa</th>
                <th class="px-4 py-3 text-left font-medium">Perfil</th>
                <th class="px-4 py-3 text-center font-medium">Ativo</th>
                <th class="px-4 py-3 text-center font-medium">Padrao</th>
                <th class="px-4 py-3 text-center font-medium">Credito ICMS</th>
                <th class="px-4 py-3 text-center font-medium">Credito PIS/COFINS</th>
                <th class="px-4 py-3 text-center font-medium">IPI no custo</th>
                <th class="px-4 py-3 text-right font-medium">Imposto venda (%)</th>
                <th class="px-4 py-3 text-right font-medium">Acoes</th>
              </tr>
            </thead>
            <tbody class="divide-y divide-slate-100 bg-white text-slate-700">
              <tr v-for="profile in tax_profiles || []" :key="profile.id">
                <td class="px-4 py-3">{{ profile.company }}</td>
                <td class="px-4 py-3">{{ profile.name }}</td>
                <td class="px-4 py-3 text-center">{{ profile.active ? "Sim" : "Nao" }}</td>
                <td class="px-4 py-3 text-center">{{ profile.default ? "Sim" : "Nao" }}</td>
                <td class="px-4 py-3 text-center">{{ profile.credit_icms ? "Sim" : "Nao" }}</td>
                <td class="px-4 py-3 text-center">{{ profile.credit_pis_cofins ? "Sim" : "Nao" }}</td>
                <td class="px-4 py-3 text-center">{{ profile.ipi_included_in_cost ? "Sim" : "Nao" }}</td>
                <td class="px-4 py-3 text-right">{{ profile.estimated_sales_tax_percent }}</td>
                <td class="px-4 py-3 text-right">
                  <div class="flex justify-end gap-2">
                    <Link
                      v-if="profile.can_edit"
                      :href="`/tax_profiles/${profile.id}/edit`"
                      class="rounded-md border border-slate-300 px-3 py-1.5 text-xs font-medium text-slate-700 transition hover:bg-slate-100"
                    >
                      Editar
                    </Link>
                    <UiButton
                      v-if="profile.can_destroy"
                      size="sm"
                      variant="danger"
                      @click="destroyProfile(profile.id)"
                    >
                      Excluir
                    </UiButton>
                  </div>
                </td>
              </tr>
              <tr v-if="(tax_profiles || []).length === 0">
                <td colspan="9" class="px-4 py-6 text-center text-slate-500">Nenhum perfil fiscal cadastrado ainda.</td>
              </tr>
            </tbody>
          </table>
        </div>
      </section>
    </div>
  </AppShell>
</template>
